/**
 * Scheduled booking slot enumeration — Scheduled Flow 2 / 2A, confirmed by
 * OQ-25 on 2026-08-02. SBOOK-01.
 *
 * The source is emphatic that this is ENUMERATE-AND-FILTER over a fixed list,
 * not free-form time arithmetic: ten one-hour windows (2–3 PM … 11 PM–12 AM)
 * across four date options, with slots removed by rule. Generating times
 * arithmetically is how a 1:30 PM or a 12:30 AM slot appears in a UI that the
 * operation cannot serve.
 *
 * This module decides WHICH WINDOWS EXIST. Whether a window is bookable is a
 * capacity question answered elsewhere (an eligible therapist from
 * `therapist-eligibility.ts` plus a complete rider plan from
 * `route-capacity.ts`), and the answer is advisory until the server revalidates.
 */

import { toManila } from "./manila-time.ts";

/**
 * The ten windows, by Manila start hour. 23 is the last: "keep only slots
 * ending on or before 12:00 AM" (Flow 2A) is a statement about THESE windows,
 * which is why the list stops here rather than a rule computing it.
 */
export const SCHEDULED_SLOT_START_HOURS = [14, 15, 16, 17, 18, 19, 20, 21, 22, 23] as const;

/** The approved first bookable hour — 2 PM (Flow 2/2A, OQ-25). */
export const DEFAULT_BOOKING_START_HOUR = 14;

/**
 * The windows for a given first hour, 0243.
 *
 * ⚠ ONLY THE START MOVES. The list always ends at 23, because Flow 2A's "keep
 * only slots ending on or before 12:00 AM" describes these windows rather than
 * a rule that computes them. A configurable END would need that rule
 * re-derived, not re-typed.
 *
 * `startHour` comes from `operating_settings.booking_start_hour`, which a
 * Superadmin owns and which defaults to the approved 14 — so the enumerate-and-
 * filter guarantee this module exists to give is intact: the list is still
 * fixed at the moment it is read, and still cannot produce a 1:30 PM window.
 * What changed is who decides where it starts.
 */
export function scheduledSlotStartHours(
  startHour: number = DEFAULT_BOOKING_START_HOUR,
): number[] {
  if (!Number.isInteger(startHour) || startHour < 0 || startHour > 23) {
    throw new RangeError("startHour must be an integer 0–23");
  }
  const hours: number[] = [];
  for (let h = startHour; h <= 23; h++) hours.push(h);
  return hours;
}

/** Today, Tomorrow, Day After, Fourth Day — each shown with its real date. */
export const SCHEDULED_DATE_OPTION_COUNT = 4;

/**
 * Same-day lead time. Was 120 (OQ-06); dropped to 0 per Leon/Luigi
 * 2026-09-23 — a same-day slot is offered as soon as its start hasn't
 * passed yet, no fixed buffer beyond that. The constant stays (rather than
 * inlining `0`) so the cutoff math below keeps reading as a lead-time rule,
 * not a magic zero.
 */
export const SAME_DAY_LEAD_MINUTES = 0;

/**
 * The Philippines has observed no DST since 1978, so a Manila wall-clock time
 * maps to exactly one instant at a fixed offset. `toManila()` (Intl-based)
 * remains the authority for the reverse direction; this constant only builds
 * instants FROM a Manila date and hour.
 */
export const MANILA_UTC_OFFSET = "+08:00";

export interface ScheduledSlotWindow {
  /** Manila calendar date this window belongs to, `YYYY-MM-DD`. */
  date: string;
  /** Manila start hour. 14–23 by default; the floor is a setting (0243). */
  startHour: number;
  /** ISO instant the window opens. */
  startsAt: string;
  /** ISO instant the window closes — exactly one hour later. */
  endsAt: string;
}

export interface ScheduledDateOption {
  /** Manila calendar date, `YYYY-MM-DD` — always shown, never hidden. */
  date: string;
  /** How far ahead this option sits: 0 = Today … 3 = Fourth Day. */
  offsetDays: number;
  /**
   * Windows that survived the rules. EMPTY IS A VALID, VISIBLE STATE: Flow 2A
   * says a date with no schedule or no available slot shows "Fully Booked" and
   * is unselectable — it is never removed from the list.
   */
  slots: ScheduledSlotWindow[];
}

function manilaInstant(date: string, hour: number): string {
  const hh = String(hour).padStart(2, "0");
  return new Date(`${date}T${hh}:00:00${MANILA_UTC_OFFSET}`).toISOString();
}

/** Add whole days to a `YYYY-MM-DD` Manila date, month/year rollover included. */
export function addManilaDays(date: string, days: number): string {
  const ms = Date.parse(`${date}T00:00:00${MANILA_UTC_OFFSET}`);
  if (!Number.isFinite(ms)) throw new RangeError("date must be YYYY-MM-DD");
  const shifted = new Date(ms + days * 86_400_000);
  // Read the shifted instant back in Manila rather than UTC — at +08:00 a plain
  // UTC read would roll the date over eight hours early.
  return toManila(shifted.toISOString(), "date").date;
}

/**
 * The four date options with their surviving windows.
 *
 * Same-day rule: a slot is offered only if it hasn't started yet — cutoff is
 * `now + SAME_DAY_LEAD_MINUTES` (0). At 8:30 PM the 8–9 PM window has already
 * started and disappears, but 9–10 PM is offered immediately; there is no
 * longer a fixed buffer past "hasn't started." (Reversed 2026-09-23 — OQ-06's
 * original 2-hour buffer is gone, per Leon/Luigi.)
 *
 * Later dates keep all ten windows — lead time is a same-day concern only.
 */
export function buildScheduledDateOptions(
  nowIso: string,
  firstHour: number = DEFAULT_BOOKING_START_HOUR,
): ScheduledDateOption[] {
  const now = toManila(nowIso, "nowIso");
  const cutoffMs = Date.parse(nowIso) + SAME_DAY_LEAD_MINUTES * 60_000;
  const hours = scheduledSlotStartHours(firstHour);

  const options: ScheduledDateOption[] = [];
  for (let offsetDays = 0; offsetDays < SCHEDULED_DATE_OPTION_COUNT; offsetDays++) {
    const date = offsetDays === 0 ? now.date : addManilaDays(now.date, offsetDays);
    const slots: ScheduledSlotWindow[] = [];

    for (const startHour of hours) {
      const startsAt = manilaInstant(date, startHour);
      if (offsetDays === 0 && Date.parse(startsAt) < cutoffMs) continue;
      slots.push({
        date,
        startHour,
        startsAt,
        // The 11 PM window closes at the next Manila midnight. Expressed as the
        // following day's 00:00 rather than "24:00" — ISO allows the latter but
        // JS Date parsing does not reliably accept it.
        endsAt:
          startHour === 23
            ? manilaInstant(addManilaDays(date, 1), 0)
            : manilaInstant(date, startHour + 1),
      });
    }

    options.push({ date, offsetDays, slots });
  }
  return options;
}

/**
 * Does this instant fall inside a window? Used when checking that a therapist's
 * or rider's earliest valid start can actually begin within the slot — the
 * source's test for Available is "can BEGIN within the window", not "is free
 * for the whole hour".
 */
export function startsWithinWindow(instantIso: string, window: ScheduledSlotWindow): boolean {
  const ms = Date.parse(instantIso);
  if (!Number.isFinite(ms)) throw new RangeError("instantIso must be a valid ISO instant");
  return ms >= Date.parse(window.startsAt) && ms < Date.parse(window.endsAt);
}
